import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, addDoc, deleteDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserAuth } from '../Context/UserAuthContext';
import { generateLegalResponse, isGeminiConfigured } from '../services/geminiService';

// Fallback response if AI is not configured
const getFallbackResponse = () => {
  return "⚠️ AI service is currently not configured. This is a demo response.\n\nBased on Pakistani law and the Pakistan Penal Code (PPC), your query requires professional legal consultation. Please ensure the Gemini API key is added to the .env file for AI-powered responses.\n\nFor now, please consult with a licensed attorney for accurate legal advice specific to your situation.\n\n📌 To enable AI responses, add your free Gemini API key to the .env file. See GEMINI_API_SETUP.md for instructions.";
};

// Hook to fetch queries for current user
export const useUserQueries = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useUserAuth();

  const fetchUserQueries = async () => {
    if (!user) {
      setQueries([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const queriesRef = collection(db, 'queries');
      // Only filter by userId - sorting done client-side to avoid index requirement
      const q = query(queriesRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      const queriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt on client side
      queriesData.sort((a, b) => {
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA; // Newest first
      });
      
      setQueries(queriesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching user queries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserQueries();
  }, [user]);

  return { queries, loading, error, refetch: fetchUserQueries };
};

// Function to create a new query with AI response
export const createQuery = async (userId, userName, queryText, lawyerType = 'General') => {
  try {
    console.log("🔵 Creating query...");
    console.log("User ID:", userId);
    console.log("Query Text:", queryText);
    console.log("Lawyer Type:", lawyerType);
    
    const queriesRef = collection(db, 'queries');
    
    // Generate AI response using Google Gemini
    let aiResponse;
    let generatedBy;
    
    console.log("🔵 Checking if Gemini is configured:", isGeminiConfigured());
    
    if (isGeminiConfigured()) {
      // Use real AI
      console.log("🟢 Using Google Gemini AI");
      aiResponse = await generateLegalResponse(queryText, lawyerType);
      generatedBy = 'Google Gemini AI';
    } else {
      // Use fallback response
      console.log("🟡 Using fallback response (API key not configured)");
      aiResponse = getFallbackResponse();
      generatedBy = 'Fallback';
    }
    
    console.log("🔵 AI Response generated, length:", aiResponse.length);
    
    const newQuery = await addDoc(queriesRef, {
      userId,
      userName,
      query: queryText,
      lawyerType,
      response: aiResponse,
      status: 'answered',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      generatedBy
    });
    
    console.log("✅ Query saved to Firestore with ID:", newQuery.id);
    
    return { success: true, id: newQuery.id, response: aiResponse };
  } catch (err) {
    console.error('❌ Error creating query:', err);
    return { success: false, error: err.message };
  }
};

// Function to update a query with new AI response
export const updateQuery = async (queryId, queryText, lawyerType) => {
  try {
    const queryRef = doc(db, 'queries', queryId);
    
    // Generate new AI response using Google Gemini
    let aiResponse;
    let generatedBy;
    
    if (isGeminiConfigured()) {
      // Use real AI
      aiResponse = await generateLegalResponse(queryText, lawyerType);
      generatedBy = 'Google Gemini AI';
    } else {
      // Use fallback response
      aiResponse = getFallbackResponse();
      generatedBy = 'Fallback';
    }
    
    await updateDoc(queryRef, {
      query: queryText,
      lawyerType,
      response: aiResponse,
      updatedAt: serverTimestamp(),
      generatedBy
    });
    
    return { success: true };
  } catch (err) {
    console.error('Error updating query:', err);
    return { success: false, error: err.message };
  }
};

// Function to delete a query
export const deleteQuery = async (queryId) => {
  try {
    await deleteDoc(doc(db, 'queries', queryId));
    return { success: true };
  } catch (err) {
    console.error('Error deleting query:', err);
    return { success: false, error: err.message };
  }
};
